/**
 * Which structures on a deal already have an approved supplier quote for a work
 * type. Shared by the Project Hub "Release to suppliers" modal (offer a
 * reference release instead of a new quoting release) and the Finance forecast
 * coverage warnings.
 *
 * Reference releases/packages are never priced, so they are ignored here.
 */

import type {
  RfqPackage,
  RfqProject,
  RfqSubmission,
  TimberSupplierStructureRef,
  VendorWorkType,
} from '../types';
import { isReferenceRelease } from './rfqReleasePurpose';
import {
  isTimberQuoteFullScope,
  normalizeQuotedStructures,
  submissionCoversStructureIds,
  timberQuoteAssignableStructures,
} from './timberQuoteStreamKey';
import {
  isPrePortalReleaseId,
  PRE_PORTAL_RELEASE_ID,
  releaseKeyForPackage,
} from './timberRfqPrePortalVersion';
import { normalizeWorkType } from './vendorHelpers';

export type RfqApprovedQuoteCoverage = {
  /** At least one approved quote / awarded supplier exists on this deal + work type. */
  hasApprovedQuote: boolean;
  /** An award covers the whole job (no structure scope known) — every structure counts as covered. */
  fullScope: boolean;
  coveredStructureIds: string[];
  coveredStructureNames: string[];
  approvedOrgIds: string[];
};

const EMPTY_COVERAGE: RfqApprovedQuoteCoverage = {
  hasApprovedQuote: false,
  fullScope: false,
  coveredStructureIds: [],
  coveredStructureNames: [],
  approvedOrgIds: [],
};

/** Approved submission ids stamped anywhere on the project (global + per-release). */
export function approvedSubmissionIdsOnRfqProject(project: RfqProject): Set<string> {
  const ids = new Set<string>();
  for (const id of project.approvedSubmissionIds ?? []) if (id) ids.add(id);
  for (const list of Object.values(project.approvedSubmissionIdsByRelease ?? {})) {
    for (const id of list ?? []) if (id) ids.add(id);
  }
  return ids;
}

/**
 * Awarded supplier orgs → release keys the award is stamped on. An empty set
 * means the award is project-level (legacy `approvedSupplierOrgId` only).
 */
export function awardedReleaseKeysByOrg(project: RfqProject): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  const add = (orgId: string | undefined | null, releaseId?: string) => {
    if (!orgId) return;
    const set = out.get(orgId) ?? new Set<string>();
    if (releaseId) set.add(isPrePortalReleaseId(releaseId) ? PRE_PORTAL_RELEASE_ID : releaseId);
    out.set(orgId, set);
  };
  add(project.approvedSupplierOrgId);
  for (const [releaseId, orgId] of Object.entries(project.approvedSupplierOrgIdByRelease ?? {})) {
    add(orgId, releaseId);
  }
  for (const [releaseId, orgIds] of Object.entries(project.approvedSupplierOrgIdsByRelease ?? {})) {
    for (const orgId of orgIds ?? []) add(orgId, releaseId);
  }
  return out;
}

/**
 * Quoting packages (never reference packages) published to one supplier on a
 * project. When `releaseKeys` is non-empty, only packages on those releases are
 * returned — falling back to every quoting package when none match (older
 * awards may predate per-release stamping).
 */
export function quotePackagesForOrg(
  projectId: string | undefined,
  orgId: string,
  rfqPackages: RfqPackage[],
  releaseKeys?: Set<string> | null,
): RfqPackage[] {
  if (!projectId) return [];
  const all = rfqPackages.filter(
    (pkg) => pkg.rfqProjectId === projectId && pkg.supplierOrgId === orgId && !isReferenceRelease(pkg),
  );
  if (!releaseKeys?.size) return all;
  const onRelease = all.filter((pkg) => {
    if (releaseKeys.has(releaseKeyForPackage(pkg))) return true;
    return (pkg.hubFileReleaseIds ?? []).some((id) => releaseKeys.has(id));
  });
  return onRelease.length ? onRelease : all;
}

/**
 * Union of the structures a supplier was asked to quote across the given
 * packages (falls back to the invite / payload when no package carries scope).
 * Empty = whole job.
 */
export function assignableStructuresAcrossPackages(
  project: RfqProject,
  orgId: string,
  packages: RfqPackage[],
): TimberSupplierStructureRef[] {
  if (!packages.length) return timberQuoteAssignableStructures(project, orgId, undefined);
  const refs: TimberSupplierStructureRef[] = [];
  let anyUnscoped = false;
  for (const pkg of packages) {
    const assignable = timberQuoteAssignableStructures(project, orgId, pkg);
    if (!assignable.length) anyUnscoped = true;
    refs.push(...assignable);
  }
  if (anyUnscoped) return [];
  return normalizeQuotedStructures(refs);
}

type SubmissionCoverage = {
  /** Structures this approved quote prices; empty = whole job. */
  refs: TimberSupplierStructureRef[];
  /** True when the quote covers everything and no structure scope is known. */
  fullScope: boolean;
};

/**
 * Structures one approved submission prices. A full-scope quote inherits the
 * scope the supplier was asked to quote (package / invite); a structure-split
 * quote is clipped to that scope when one is known.
 */
function approvedSubmissionCoverage(
  project: RfqProject,
  sub: RfqSubmission,
  rfqPackages: RfqPackage[],
  releaseKeys?: Set<string> | null,
): SubmissionCoverage {
  const direct = sub.rfqPackageId ? rfqPackages.find((row) => row.id === sub.rfqPackageId) : undefined;
  const packages = direct && !isReferenceRelease(direct)
    ? [direct]
    : quotePackagesForOrg(project.id, sub.supplierOrgId, rfqPackages, releaseKeys);
  const assignable = assignableStructuresAcrossPackages(project, sub.supplierOrgId, packages);
  if (isTimberQuoteFullScope(sub)) {
    return { refs: assignable, fullScope: !assignable.length };
  }
  const quoted = normalizeQuotedStructures(sub.quotedStructures);
  const ids = new Set(submissionCoversStructureIds(sub, assignable.map((s) => s.id)));
  return { refs: quoted.filter((s) => !assignable.length || ids.has(s.id)), fullScope: false };
}

/**
 * Structures an awarded supplier's approved quote(s) cover on one RFQ project —
 * the structures a timber/CNC bill from that supplier pays for. Prefers the
 * explicit submission when given, then every approved submission from the org,
 * then the awarded release scope. Empty = whole job (or no award found).
 */
export function approvedStructuresForSupplierOnProject(input: {
  project: RfqProject;
  supplierOrgId: string;
  rfqPackages: RfqPackage[];
  rfqSubmissions: RfqSubmission[];
  submissionId?: string | null;
}): TimberSupplierStructureRef[] {
  const { project, supplierOrgId, rfqPackages, rfqSubmissions } = input;
  const awardedByOrg = awardedReleaseKeysByOrg(project);
  const releaseKeys = awardedByOrg.get(supplierOrgId);
  const approvedIds = approvedSubmissionIdsOnRfqProject(project);

  const explicit = input.submissionId
    ? rfqSubmissions.find((sub) => sub.id === input.submissionId && sub.supplierOrgId === supplierOrgId)
    : undefined;
  if (explicit) {
    return approvedSubmissionCoverage(project, explicit, rfqPackages, releaseKeys).refs;
  }

  const approvedSubs = rfqSubmissions.filter((sub) => {
    if (!sub.id || sub.supplierOrgId !== supplierOrgId || !approvedIds.has(sub.id)) return false;
    return !sub.rfqProjectId || !project.id || sub.rfqProjectId === project.id;
  });
  if (approvedSubs.length) {
    const refs: TimberSupplierStructureRef[] = [];
    for (const sub of approvedSubs) {
      const coverage = approvedSubmissionCoverage(project, sub, rfqPackages, releaseKeys);
      if (coverage.fullScope) return [];
      refs.push(...coverage.refs);
    }
    return normalizeQuotedStructures(refs);
  }

  if (!awardedByOrg.has(supplierOrgId)) return [];
  const packages = quotePackagesForOrg(project.id, supplierOrgId, rfqPackages, releaseKeys);
  return assignableStructuresAcrossPackages(project, supplierOrgId, packages);
}

export function approvedQuoteCoverageForDeal(input: {
  dealId: string;
  workType: VendorWorkType;
  rfqProjects: RfqProject[];
  rfqPackages: RfqPackage[];
  rfqSubmissions: RfqSubmission[];
}): RfqApprovedQuoteCoverage {
  const workType = normalizeWorkType(input.workType);
  const projects = input.rfqProjects.filter(
    (p) => p.dealId === input.dealId && normalizeWorkType(p.workType) === workType,
  );
  if (!projects.length) return EMPTY_COVERAGE;

  const covered = new Map<string, string>();
  const approvedOrgIds = new Set<string>();
  let fullScope = false;
  let hasApprovedQuote = false;

  const addRefs = (refs: TimberSupplierStructureRef[]) => {
    for (const ref of refs) {
      if (!ref.id) continue;
      if (!covered.has(ref.id)) covered.set(ref.id, ref.name || ref.id);
    }
  };

  for (const project of projects) {
    const approvedIds = approvedSubmissionIdsOnRfqProject(project);
    const awardedByOrg = awardedReleaseKeysByOrg(project);
    const approvedSubs = input.rfqSubmissions.filter((sub) => {
      if (!sub.id || !approvedIds.has(sub.id)) return false;
      if (sub.rfqProjectId && project.id && sub.rfqProjectId !== project.id) return false;
      return true;
    });
    const orgsWithApprovedSubs = new Set<string>();

    for (const sub of approvedSubs) {
      hasApprovedQuote = true;
      approvedOrgIds.add(sub.supplierOrgId);
      orgsWithApprovedSubs.add(sub.supplierOrgId);
      const coverage = approvedSubmissionCoverage(
        project,
        sub,
        input.rfqPackages,
        awardedByOrg.get(sub.supplierOrgId),
      );
      if (coverage.fullScope) fullScope = true;
      else addRefs(coverage.refs);
    }

    for (const [orgId, releaseKeys] of Array.from(awardedByOrg.entries())) {
      if (orgsWithApprovedSubs.has(orgId)) continue;
      hasApprovedQuote = true;
      approvedOrgIds.add(orgId);
      const packages = quotePackagesForOrg(project.id, orgId, input.rfqPackages, releaseKeys);
      const assignable = assignableStructuresAcrossPackages(project, orgId, packages);
      if (!assignable.length) fullScope = true;
      else addRefs(assignable);
    }
  }

  return {
    hasApprovedQuote,
    fullScope,
    coveredStructureIds: Array.from(covered.keys()),
    coveredStructureNames: Array.from(covered.values()),
    approvedOrgIds: Array.from(approvedOrgIds),
  };
}

export type ApprovedCoverageMatch = 'all' | 'some' | 'none';

function normName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * How much of a file stream's structure tags is already covered by an approved
 * quote. Untagged streams (no structures) match the whole job, so any approval
 * counts as `all`.
 */
export function approvedCoverageForStructures(
  coverage: RfqApprovedQuoteCoverage,
  structureIds: ReadonlyArray<string> | null | undefined,
  structureNames: ReadonlyArray<string> | null | undefined,
): ApprovedCoverageMatch {
  if (!coverage.hasApprovedQuote) return 'none';
  if (coverage.fullScope) return 'all';
  const ids = (structureIds ?? []).map((s) => (s || '').trim()).filter(Boolean);
  const names = (structureNames ?? []).map((s) => (s || '').trim()).filter(Boolean);
  if (!ids.length && !names.length) {
    return coverage.coveredStructureIds.length ? 'all' : 'none';
  }
  const coveredIds = new Set(coverage.coveredStructureIds);
  const coveredNames = new Set(coverage.coveredStructureNames.map(normName));
  const total = Math.max(ids.length, names.length);
  let hits = 0;
  for (let i = 0; i < total; i += 1) {
    const id = ids[i];
    const name = names[i];
    if ((id && coveredIds.has(id)) || (name && coveredNames.has(normName(name)))) hits += 1;
  }
  if (hits === 0) return 'none';
  return hits >= total ? 'all' : 'some';
}
